<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ConfirmSyncInvoice',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const labelAlign = ref('right');
  const payload = ref({
    id: props.invoice.id,
    print: true,
    print_copies: 1,
    target: 'remote',
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    payload: payload.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'payload' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      labelAlign,
      payload,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('ConfirmSyncInvoice.Title', 'Create invoice'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_axfh4fnka_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_4186377094414664_closeModal_qjvjp257y_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_4186377094414664_request_9g0nng53a_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'issue_invoice',
      data: payload.value,
    };
  };

  const act_7315092382398562_closeModal_axfh4fnka =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_axfh4fnka_getActionArgs,
    });
  const inst_evh_7315092382398562_close_87mwnsryz =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_axfh4fnka,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_87mwnsryz],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_r43a0juz7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_mjfaunhwp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_r43a0juz7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_mjfaunhwp],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_88bnajio5 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_a2jbre7e9 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_88bnajio5,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_a2jbre7e9],
    event: 'close',
  });
  const act_4186377094414664_closeModal_qjvjp257y =
    new actionTypes.CloseModalAction({
      actionArgs: act_4186377094414664_closeModal_qjvjp257y_getActionArgs,
    });
  const inst_evh_4186377094414664_success_odki41wzn =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_closeModal_qjvjp257y,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_4186377094414664_request_9g0nng53a = new actionTypes.RequestAction({
    actionArgs: act_4186377094414664_request_9g0nng53a_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_4186377094414664_success_odki41wzn],
  });
  const inst_evh_4186377094414664_submit_c787v79ep =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_request_9g0nng53a,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_4186377094414664_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4186377094414664_submit_c787v79ep],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_vfyjtgfkd =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_e25dxxrpm =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_vfyjtgfkd,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_e25dxxrpm],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_87mwnsryz,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_mjfaunhwp,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_a2jbre7e9,
    inst_evh_8564662037462133_close,
    inst_evh_4186377094414664_success_odki41wzn,
    inst_evh_4186377094414664_submit_c787v79ep,
    submit: inst_evh_4186377094414664_submit_c787v79ep,
    inst_evh_4186377094414664_submit,
    inst_evh_2434514588044777_clickPreventStop_e25dxxrpm,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_87mwnsryz = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_87mwnsryz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_mjfaunhwp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_mjfaunhwp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_a2jbre7e9 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_a2jbre7e9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_success_odki41wzn = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_success_odki41wzn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit_c787v79ep = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit_c787v79ep.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_e25dxxrpm = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_e25dxxrpm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ConfirmSyncInvoice"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <p class="d-flex align-items-start gap-2 mb-0">
          <ItpIcon fa="info-circle"> </ItpIcon>
          <ItpText
            wrap
            :text="
              pgettext(
                'InvoiceSync',
                'After synchronization, the invoice can no longer be modified.'
              )
            "
          >
          </ItpText>
        </p>
      </ItpAlert>
      <ItpForm
        :id="formId"
        name="confirmSyncInvoice"
        ref="mainForm"
        @submit="evh_4186377094414664_submit($event, {})"
      >
        <ItpFormGroup
          name="print"
          :label="pgettext('InvoiceSync', 'Print')"
          :labelAlign="labelAlign"
          :label-cols-md="6"
          :content-cols-md="6"
        >
          <ItpFormCheckbox
            name="print"
            v-model="payload.print"
            required
            size="md"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="print_copies"
          :label="pgettext('InvoiceSync', 'Copies')"
          :labelAlign="labelAlign"
          :label-cols-md="6"
          :content-cols-md="6"
        >
          <ItpFormInput
            type="number"
            name="print_copies"
            v-model="payload.print_copies"
            required
            :min="1"
            :disabled="!payload.print"
          >
          </ItpFormInput>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Create invoice')"
          :disabled="
            !forms.confirmSyncInvoice || !forms.confirmSyncInvoice.submittable
          "
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
